import type {
  ClaimLineItemsDto,
  CreateExpenseDto,
  ExpenseLineItem,
  ExpenseResponseDto,
  LineItemSplitResult,
  LineItemsRest,
  ReceiptScanLineItemDto,
  SplitMethod,
  UnpickedSplit,
  UpdateExpenseDto,
} from '@splitfiesta/shared';
import {
  dropSurplusPicks,
  fromMinorUnits,
  maxUnitsOf,
  normalizeLegacyPicks,
  pickersOf,
  roundToCurrencyPrecision,
  splitByLineItems,
  stakeOf,
  toMinorUnits,
  unitsLeft,
  withUnits,
} from '@splitfiesta/shared';

import { EXAMPLE_GROUP_ID } from '../fixtures/exampleGroup';
import { queryKeys } from '../queries/queryKeys';

/** The prefixes every split change invalidates; each app loops invalidateQueries over them (core has no react-query). expense(id) is set by the caller from the response. */
export const SPLIT_CHANGE_KEYS = (groupId: string) =>
  [
    queryKeys.expensesByGroupAll(groupId),
    queryKeys.groupWithDashboard(groupId),
    queryKeys.balances(),
    queryKeys.groups(),
    queryKeys.allActivityFeeds(),
    queryKeys.groupReports.all(groupId),
  ] as const;

export interface ItemsFormState {
  itemsMode: boolean;
  lineItems: ExpenseLineItem[];
  rest: LineItemsRest;
  participants: string[];
  fromScan: boolean;
  /** Clears on any edit of the list. */
  scanReconciled: boolean;
  /**
   * The scan's lines did not add up to its total and the list is untouched
   * since: the only source for the "don't add up" warning. Picks and the
   * rest mode leave it alone; any list edit clears it.
   */
  scanUnreconciled: boolean;
}

const DEFAULT_REST: LineItemsRest = { mode: 'proportional' };

/**
 * Itemizing is free, but like every Pro-era surface it stays behind the `pro`
 * flag until the Pro release. The example group, whose sandbox holds no
 * items, never itemizes. An existing itemized expense still opens read-only.
 */
export function itemsAvailableIn(
  group: { id: string } | null | undefined,
  proFlagOn: boolean
): boolean {
  return proFlagOn && group?.id !== EXAMPLE_GROUP_ID;
}

/**
 * The group's default split as the weights for what nobody picked, over the
 * expense's participants only (the server rejects a weight for anyone
 * else). Equal when the group has no default or no participant has a weight.
 */
export function unpickedSplitFromGroup(
  group:
    | {
        defaultSplitMethod?: string | null;
        defaultSplitConfig?: {
          percentages?: Record<string, number>;
          shares?: Record<string, number>;
        } | null;
      }
    | null
    | undefined,
  participants: readonly string[]
): UnpickedSplit {
  const method = group?.defaultSplitMethod;
  const source =
    method === 'percentage'
      ? group?.defaultSplitConfig?.percentages
      : method === 'shares'
        ? group?.defaultSplitConfig?.shares
        : undefined;
  const weights: Record<string, number> = {};
  for (const id of participants) {
    const weight = source?.[id];
    if (typeof weight === 'number' && Number.isFinite(weight) && weight >= 0)
      weights[id] = weight;
  }
  if (
    (method !== 'percentage' && method !== 'shares') ||
    Object.keys(weights).length === 0
  )
    return { method: 'equal', weights: {} };
  return { method, weights };
}

/**
 * An edit keeps the stored weights, so the group's later default never
 * reprices the bill; only a participant change touches them: removed people
 * drop out, added people take the group's current default weight.
 */
export function unpickedSplitForEdit(
  stored: UnpickedSplit | null | undefined,
  group: Parameters<typeof unpickedSplitFromGroup>[0],
  participants: readonly string[],
  storedParticipants: readonly string[]
): UnpickedSplit {
  if (!stored || stored.method === 'equal')
    return { method: 'equal', weights: {} };
  const before = new Set(storedParticipants);
  const unchanged =
    participants.length === before.size &&
    participants.every(id => before.has(id));
  if (unchanged)
    return { method: stored.method, weights: { ...stored.weights } };
  const defaults = unpickedSplitFromGroup(
    group && { ...group, defaultSplitMethod: stored.method },
    participants.filter(id => !before.has(id))
  ).weights;
  const weights: Record<string, number> = {};
  for (const id of participants) {
    const weight = before.has(id) ? stored.weights[id] : defaults[id];
    if (weight !== undefined) weights[id] = weight;
  }
  return { method: stored.method, weights };
}

/**
 * Null while the form cannot be split yet (no amount, nobody included, an
 * item amount mid-edit); the UIs then draw no preview.
 */
export function previewLineItemSplit(
  state: ItemsFormState,
  amount: number,
  currency: string,
  entry?: { amount: number; currency: string },
  unpickedSplit?: UnpickedSplit
): LineItemSplitResult | null {
  // The splitter rounds silently, the server rejects: no preview means no save.
  const itemCurrency = entry?.currency ?? currency;
  if (state.lineItems.some(item => !atPrecision(item.amount, itemCurrency))) {
    return null;
  }
  try {
    return splitByLineItems({
      amount,
      currency,
      entryAmount: entry?.amount,
      entryCurrency: entry?.currency,
      participants: state.participants,
      items: state.lineItems,
      rest: state.rest,
      unpickedSplit,
    });
  } catch (err) {
    if (err instanceof RangeError) return null;
    throw err;
  }
}

/** How a row reads for one person. `left` is what nobody holds yet, so on `mine` it is what others may still take. */
export type ItemAvailability =
  | { kind: 'free'; quantity: number; left: number }
  | { kind: 'mine'; quantity: number; mine: number; left: number }
  | { kind: 'partial'; quantity: number; left: number; takenBy: string[] }
  | { kind: 'taken'; quantity: number; takenBy: string[] };

/** Pickers outside the split are ignored here as the splitter ignores them, and the server rejects them on save. */
function withoutGhosts(
  item: ExpenseLineItem,
  inSplit: ReadonlySet<string>
): ExpenseLineItem {
  if (!item.picks)
    return { ...item, sharedBy: item.sharedBy.filter(id => inSplit.has(id)) };
  const picks = item.picks.filter(p => inSplit.has(p.userId));
  return { ...item, picks, sharedBy: picks.map(p => p.userId) };
}

/** A legacy row reads as stored: each sharer in the split holds one unit. */
export function availabilityOf(
  item: ExpenseLineItem,
  userId: string,
  participants: readonly string[]
): ItemAvailability {
  const inSplit = new Set(participants);
  const picks = item.picks
    ? item.picks.filter(p => inSplit.has(p.userId))
    : [...new Set(item.sharedBy)]
        .filter(id => inSplit.has(id))
        .map(id => ({ userId: id, units: 1 }));
  const { quantity } = item;
  const mine = picks.find(p => p.userId === userId)?.units ?? 0;
  const left = Math.max(0, quantity - picks.reduce((n, p) => n + p.units, 0));
  const takenBy = picks.filter(p => p.userId !== userId).map(p => p.userId);
  if (mine > 0) return { kind: 'mine', quantity, mine, left };
  if (takenBy.length === 0) return { kind: 'free', quantity, left };
  if (left === 0) return { kind: 'taken', quantity, takenBy };
  return { kind: 'partial', quantity, left, takenBy };
}

/**
 * Sets `userId`'s units on one row, clamped to what they may hold. Returns
 * `items` itself when nothing changes, including a take that does not fit
 * beside a legacy row's other sharers. A legacy row stays legacy until the
 * person takes units on it (the server's claim rule, `withUnits`).
 */
export function takeUnits(
  items: ExpenseLineItem[],
  itemId: string,
  userId: string,
  units: number,
  participants: readonly string[]
): ExpenseLineItem[] {
  if (!Number.isFinite(units)) return items;
  const index = items.findIndex(i => i.id === itemId);
  if (index < 0) return items;
  const stored = items[index];
  const stake = stakeOf(stored, userId);
  const row = withoutGhosts(stored, new Set(participants));
  // A legacy sharer past the row's quantity keeps their stake until they change it.
  const max = Math.max(stake, maxUnitsOf(row, userId));
  const target = Math.max(0, Math.min(Math.floor(units), max));
  if (target === stake) return items;
  const out = items.slice();
  out[index] = withUnits(row, userId, target);
  return out;
}

/** Takes one unit, or releases all of the person's units when they hold some. */
export function toggleUnit(
  items: ExpenseLineItem[],
  itemId: string,
  userId: string,
  participants: readonly string[]
): ExpenseLineItem[] {
  const row = items.find(i => i.id === itemId);
  if (!row) return items;
  const target = stakeOf(row, userId) > 0 ? 0 : 1;
  return takeUnits(items, itemId, userId, target, participants);
}

/** A row nobody holds; an empty legacy row would read as everyone's. */
const heldByNobody = (row: ExpenseLineItem): ExpenseLineItem =>
  !row.picks && row.sharedBy.length === 0
    ? { ...row, sharedBy: [], picks: [] }
    : row;

/** A tap on a name: one unit, one more up to the quantity, then off. Quantity 1 toggles. */
export function cycleUnit(
  items: ExpenseLineItem[],
  itemId: string,
  userId: string,
  participants: readonly string[]
): ExpenseLineItem[] {
  const index = items.findIndex(i => i.id === itemId);
  if (index < 0) return items;
  const row = items[index];
  const inSplit = new Set(participants);
  if (
    inSplit.has(userId) &&
    !row.picks &&
    !row.sharedBy.some(id => inSplit.has(id))
  ) {
    // Everyone's by default: the tap takes this person out, as on any shared row.
    const out = items.slice();
    out[index] = heldByNobody({
      ...row,
      sharedBy: participants.filter(id => id !== userId),
    });
    return out;
  }
  const stake = stakeOf(row, userId);
  const more = takeUnits(items, itemId, userId, stake + 1, participants);
  if (more !== items) return more;
  const out = items.slice();
  if (stake > 0) {
    const off = takeUnits(items, itemId, userId, 0, participants);
    out[index] = heldByNobody(off[index]);
    return out;
  }
  if (row.quantity !== 1) return items;
  // Units cannot split one item, so a second name shares it equally: the
  // row goes back to the sharers form the splitter divides evenly.
  const { picks: _picks, ...shared } = withoutGhosts(row, inSplit);
  out[index] = { ...shared, sharedBy: [...shared.sharedBy, userId] };
  return out;
}

/** Who stays in after leaving item mode: everyone who holds an item; when an item is shared by everyone, or nobody holds anything, the participants as they were. */
export function includedAfterItems(
  items: readonly ExpenseLineItem[],
  participants: readonly string[]
): string[] {
  const inSplit = new Set(participants);
  const holders = new Set<string>();
  for (const item of items) {
    const pickers = pickersOf(item).filter(id => inSplit.has(id));
    if (!item.picks && pickers.length === 0) return [...participants];
    for (const id of pickers) holders.add(id);
  }
  if (holders.size === 0) return [...participants];
  return participants.filter(id => holders.has(id));
}

/** Units nobody holds, across the list; a legacy row counts what its sharers leave over. */
export const unpickedUnits = (items: readonly ExpenseLineItem[]): number =>
  items.reduce(
    (n, i) => n + Math.max(0, unitsLeft(normalizeLegacyPicks(i))),
    0
  );

/** Items nobody holds a unit of; a legacy row with no sharers is everyone's, so it never counts. */
export const itemsHeldByNobody = (items: readonly ExpenseLineItem[]): number =>
  items.filter(i => i.picks && !i.picks.some(p => p.units > 0)).length;

/** No one in the split holds a unit; a legacy row is held by a sharer or, with none in the split, by everyone. */
export const nobodyHolds = (
  item: ExpenseLineItem,
  participants: readonly string[]
): boolean => !!item.picks && !participants.some(id => stakeOf(item, id) > 0);

/** Items, not units, that nobody in the split holds. */
export const itemsWithNobody = (
  items: readonly ExpenseLineItem[],
  participants: readonly string[]
): number => items.filter(i => nobodyHolds(i, participants)).length;

/** The viewer's full set of picks, as the claims route takes it; a legacy row they share is sent as 1 so the server keeps it as stored. */
export const claimPicksOf = (
  items: readonly ExpenseLineItem[],
  userId: string
): ClaimLineItemsDto => ({
  picks: items.flatMap(i => {
    const units = stakeOf(i, userId);
    return units > 0 ? [{ lineItemId: i.id, units }] : [];
  }),
});

/**
 * A person leaving the split loses their units on every row; a legacy row only
 * drops them from `sharedBy`, and becomes nobody's once no sharer left on it is
 * in the split (the splitter would read its outsiders as everyone). Returns
 * `items` itself when they held none.
 */
export function stripParticipantPicks(
  items: ExpenseLineItem[],
  userId: string,
  participants: readonly string[]
): ExpenseLineItem[] {
  if (!items.some(i => pickersOf(i).includes(userId))) return items;
  const staying = new Set(participants.filter(id => id !== userId));
  return items.map(i => {
    if (!pickersOf(i).includes(userId)) return i;
    const row = withUnits(i, userId, 0);
    return !row.picks && !row.sharedBy.some(id => staying.has(id))
      ? { ...row, sharedBy: [], picks: [] }
      : row;
  });
}

/** The item editor's save: a smaller quantity drops the surplus picks, heaviest picker first; a legacy row stays legacy. */
export function applyQuantity(
  item: ExpenseLineItem,
  fields: { name: string; amount: number; quantity: number }
): ExpenseLineItem {
  const edited = { ...item, ...fields };
  return fields.quantity < item.quantity
    ? dropSurplusPicks(edited, fields.quantity)
    : edited;
}

export function restLabelKind(
  restAmount: number,
  itemCount: number
): 'hidden' | 'rest' | 'restNegative' {
  if (itemCount === 0 || restAmount === 0) return 'hidden';
  return restAmount < 0 ? 'restNegative' : 'rest';
}

/**
 * No percentage of the amount: tax plus a 35 % tip must never warn, while a
 * list missing most of its lines always does. `−rest > amount` means the
 * items exceed twice the total.
 */
export function restWarning(args: {
  restAmount: number;
  itemsTotal: number;
  amount: number;
  scanReconciled: boolean;
}): 'none' | 'missingItems' | 'overTotal' {
  const { restAmount, itemsTotal, amount, scanReconciled } = args;
  if (-restAmount > amount) return 'overTotal';
  if (restAmount > itemsTotal && !scanReconciled) return 'missingItems';
  return 'none';
}

const SWITCH_CONFIRM_KEYS = {
  equal: 'switchToEqual',
  percentage: 'switchToPercentage',
  shares: 'switchToShares',
  custom: 'switchToCustom',
} as const satisfies Record<SplitMethod, string>;

/** One key per method so each locale can decline the method name. */
export function switchConfirmKey(
  method: SplitMethod
):
  'switchToEqual' | 'switchToPercentage' | 'switchToShares' | 'switchToCustom' {
  return SWITCH_CONFIRM_KEYS[method];
}

function atPrecision(amount: number, currency: string): boolean {
  if (!Number.isFinite(amount)) return true;
  return fromMinorUnits(toMinorUnits(amount, currency), currency) === amount;
}

/**
 * Item amounts at `currency`'s precision (a JPY switch drops cents). Returns
 * `items` itself when nothing changes, so a no-op keeps referential equality.
 */
export function roundLineItems(
  items: ExpenseLineItem[],
  currency: string
): ExpenseLineItem[] {
  let changed = false;
  const rounded = items.map(item => {
    if (!Number.isFinite(item.amount)) return item;
    const amount = roundToCurrencyPrecision(item.amount, currency);
    if (amount === item.amount) return item;
    changed = true;
    return { ...item, amount };
  });
  return changed ? rounded : items;
}

/** The state change for any edit of the list itself (add, edit, delete, undo). */
export function itemsListEdit(
  lineItems: ExpenseLineItem[]
): Pick<ItemsFormState, 'lineItems' | 'scanReconciled' | 'scanUnreconciled'> {
  return { lineItems, scanReconciled: false, scanUnreconciled: false };
}

/**
 * An itemized expense is stored as a custom split of the preview's splits.
 * With `currency` (the line-item currency) the amounts are rounded to it.
 */
export function toItemsPayload(
  state: ItemsFormState,
  splits: LineItemSplitResult['splits'],
  currency?: string,
  unpickedSplit?: UnpickedSplit
): Pick<
  CreateExpenseDto,
  | 'splitMethod'
  | 'splits'
  | 'lineItems'
  | 'lineItemsRest'
  | 'lineItemParticipants'
  | 'unpickedSplit'
> {
  return {
    splitMethod: 'custom',
    splits: splits.map(s => ({ ...s })),
    lineItems: (currency
      ? roundLineItems(state.lineItems, currency)
      : state.lineItems
    ).map(item => ({
      ...item,
      name: item.name.trim(),
      sharedBy: [...pickersOf(item)],
      ...(item.picks && { picks: item.picks.map(p => ({ ...p })) }),
    })),
    lineItemsRest: { ...state.rest },
    lineItemParticipants: [...state.participants],
    ...(unpickedSplit && {
      unpickedSplit: {
        method: unpickedSplit.method,
        weights: { ...unpickedSplit.weights },
      },
    }),
  };
}

export function dropItemsPayload(): Pick<
  UpdateExpenseDto,
  'lineItems' | 'lineItemsRest' | 'lineItemParticipants'
> {
  return { lineItems: null, lineItemsRest: null, lineItemParticipants: null };
}

/**
 * Null when the expense is not itemized. A loaded list is never treated as a
 * fresh scan. Legacy rows stay legacy: the first pick on one normalises it.
 */
export function hydrateItemsState(
  expense: Pick<
    ExpenseResponseDto,
    'lineItems' | 'lineItemsRest' | 'lineItemParticipants'
  >
): ItemsFormState | null {
  if (!expense.lineItems) return null;
  return {
    itemsMode: true,
    lineItems: expense.lineItems.map(item => ({
      ...item,
      sharedBy: [...item.sharedBy],
      ...(item.picks && { picks: item.picks.map(p => ({ ...p })) }),
    })),
    rest: { ...(expense.lineItemsRest ?? DEFAULT_REST) },
    participants: [...(expense.lineItemParticipants ?? [])],
    fromScan: false,
    scanReconciled: false,
    scanUnreconciled: false,
  };
}

export interface SeedItemsOptions {
  /** The form's line-item currency; the scan's amounts may be at another precision. */
  currency?: string;
  /** The localized `lineItems.unnamed` label, given to a line read with no name. */
  unnamedLabel?: string;
}

/** Without `unnamedLabel`, an unread name stays '' and the form asks for one before save. */
export function seedItemsFromScan(
  lines: ReceiptScanLineItemDto[],
  mintId: () => string,
  reconciled: boolean,
  options: SeedItemsOptions = {}
): Pick<
  ItemsFormState,
  'lineItems' | 'fromScan' | 'scanReconciled' | 'scanUnreconciled'
> {
  const { currency, unnamedLabel } = options;
  const lineItems = lines.map(line => ({
    id: mintId(),
    name: line.name.trim() === '' && unnamedLabel ? unnamedLabel : line.name,
    amount: line.amount,
    quantity: line.quantity,
    sharedBy: [],
    picks: [],
  }));
  return {
    lineItems: currency ? roundLineItems(lineItems, currency) : lineItems,
    fromScan: true,
    scanReconciled: reconciled,
    scanUnreconciled: !reconciled,
  };
}

/** The keys only the detail cache carries; list rows never do. */
export type LineItemDetailKey =
  'lineItems' | 'lineItemsRest' | 'lineItemParticipants' | 'lineItemCurrency';

export type WithoutLineItems<T> = Omit<T, LineItemDetailKey>;

/** A list row: the expense without its line items, their rest, participants and currency. */
export function withoutLineItems<T extends object>(
  row: T
): WithoutLineItems<T> {
  const {
    lineItems: _items,
    lineItemsRest: _rest,
    lineItemParticipants: _participants,
    lineItemCurrency: _currency,
    ...listRow
  } = row as T & Partial<Record<LineItemDetailKey, unknown>>;
  return listRow;
}

/** What a member holds, for the split card: the items they have units on, in stored order; a legacy sharer holds one. */
export const memberPicksLine = (
  items: readonly ExpenseLineItem[],
  userId: string
): Array<{ name: string; units: number }> =>
  items.flatMap(i => {
    const units = stakeOf(i, userId);
    return units > 0 ? [{ name: i.name, units }] : [];
  });
